import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ChatApiService } from '@app/core/services/chat/chat-api.service';
import { ChatHubConnectionService } from '@app/core/services/hubs/chat-hub-connection.service';
import { BadisOauthLoginService } from '@app/core/services/auth/badis-oauth-login.service';
import { ToastNotifyService } from '@app/pages/notifications/services/toast-notify.service';
import { AlertType } from '@app/shared/toasts/security-alert/models/security-alert';
import { ChatUiService } from '@app/core/services/chat/chat-ui.service';
import { ChatConversationDto, ChatConversationType } from '@app/core/models/chat/chat.models';
import { SettingsService } from '@app/core/services/settings/settings.service';

@Injectable({ providedIn: 'root' })
export class ChatStateService {
    private unreadCountSubject = new BehaviorSubject<number>(0);
    public unreadCount$ = this.unreadCountSubject.asObservable();

    private initialized = false;
    private featureEnabled = false;
    private conversationIndex = new Map<string, ChatConversationDto>();
    private joinedConversations = new Set<string>();
    private aiUserId?: string | null;
    private systemUserId?: string | null;

    constructor(
        private chatApi: ChatApiService,
        private chatHub: ChatHubConnectionService,
        private authService: BadisOauthLoginService,
        private toastNotify: ToastNotifyService,
        private chatUi: ChatUiService,
        private settingsService: SettingsService
    ) { }

    public setFeatureEnabled(enabled: boolean): void {
        const nextEnabled = !!enabled;
        if (this.featureEnabled === nextEnabled) {
            return;
        }

        this.featureEnabled = nextEnabled;

        if (!this.featureEnabled) {
            this.unreadCountSubject.next(0);
            this.conversationIndex.clear();
            return;
        }

        if (this.authService.isUserLoggedIn()) {
            this.resolveSystemUsers();
            this.refreshUnreadCount();
        }
    }

    public init(): void {
        if (this.initialized) {
            return;
        }

        this.initialized = true;

        this.authService.onAuthStatusChanged.subscribe((isAuthenticated) => {
            if (!isAuthenticated) {
                this.unreadCountSubject.next(0);
                this.conversationIndex.clear();
                return;
            }

            if (!this.featureEnabled) {
                this.unreadCountSubject.next(0);
                return;
            }

            this.resolveSystemUsers();
            this.refreshUnreadCount();
        });

        this.chatHub.connectionReady.subscribe((ready) => {
            if (ready && this.featureEnabled) {
                this.refreshUnreadCount();
            }
        });

        this.chatHub.newMessage$.subscribe((message) => {
            if (!this.featureEnabled) {
                return;
            }

            const currentUserId = this.authService.userAccessToken?.userId;
            if (message?.isImportant && !message.isDeleted && (!currentUserId || message.senderId !== currentUserId)) {
                const content = (message.body ?? '').toString().trim();
                if (content) {
                    const conversationLabel = this.resolveConversationLabel(message.conversationId, message.senderId);
                    const preview = content.length > 200 ? `${content.slice(0, 200)}...` : content;
                    const toastRef = this.toastNotify.showGeneralAlert({
                        title: conversationLabel,
                        content: `Önemli mesaj: ${preview}`,
                        alertType: AlertType.Warning
                    });
                    if (toastRef?.onTap) {
                        toastRef.onTap.subscribe(() => {
                            this.chatUi.openChat(message.conversationId);
                        });
                    }
                }
            }

            if (!currentUserId || message.senderId !== currentUserId) {
                this.refreshUnreadCount();
            }
        });

        this.chatHub.read$.subscribe(() => {
            if (!this.featureEnabled) {
                return;
            }

            this.refreshUnreadCount();
        });

        if (this.featureEnabled && this.authService.isUserLoggedIn()) {
            this.resolveSystemUsers();
            this.refreshUnreadCount();
        }
    }

    public refreshUnreadCount(): void {
        if (!this.featureEnabled) {
            this.unreadCountSubject.next(0);
            return;
        }

        this.chatApi.getConversations().subscribe({
            next: (conversations) => {
                this.updateConversationIndex(conversations ?? []);
                const total = (conversations ?? []).reduce((sum, item) => sum + (item.unreadCount ?? 0), 0);
                this.unreadCountSubject.next(total);
            },
            error: () => {
                // Keep last known count on error
            }
        });
    }

    private updateConversationIndex(conversations: ChatConversationDto[]): void {
        this.conversationIndex.clear();
        conversations.forEach((conversation) => {
            if (conversation?.id) {
                this.conversationIndex.set(conversation.id, conversation);
            }
        });

        this.syncConversationSubscriptions(conversations);
    }

    private syncConversationSubscriptions(conversations: ChatConversationDto[]): void {
        const next = new Set<string>();
        conversations.forEach((conversation) => {
            if (conversation?.id) {
                next.add(conversation.id);
                if (!this.joinedConversations.has(conversation.id)) {
                    this.chatHub.joinConversation(conversation.id);
                    this.joinedConversations.add(conversation.id);
                }
            }
        });

        this.joinedConversations.forEach((conversationId) => {
            if (!next.has(conversationId)) {
                this.chatHub.leaveConversation(conversationId);
                this.joinedConversations.delete(conversationId);
            }
        });
    }

    private resolveConversationLabel(conversationId?: string, senderId?: string): string {
        const conversation = conversationId ? this.conversationIndex.get(conversationId) : undefined;
        const title = conversation?.title?.trim();
        if (title) {
            return title;
        }

        if (conversation?.type === ChatConversationType.Ai) {
            return 'AI Asistan';
        }

        if (conversation?.type === ChatConversationType.System) {
            return 'Sistem';
        }

        if (conversation?.type === ChatConversationType.Group) {
            return 'Grup Sohbeti';
        }

        if (senderId) {
            if (this.aiUserId && senderId === this.aiUserId) {
                return 'AI Asistan';
            }

            if (this.systemUserId && senderId === this.systemUserId) {
                return 'Sistem';
            }

            return `Kullanıcı ${senderId.slice(0, 8)}`;
        }

        return 'Sohbet';
    }

    private resolveSystemUsers(): void {
        this.aiUserId = this.settingsService.getSettingValue<string>('Chat.AiAssistant.UserId');
        this.systemUserId = this.settingsService.getSettingValue<string>('General.System.UserId');
    }
}
